import { useState, useEffect, useRef } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { User, DiceThree, Crown, Ghost, Camera, Trash, Lock } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { sortearAtributos } from '@/lib/sortearAtributos'
import { ImageCropDialog } from '@/components/account/ImageCropDialog'
import type { Character } from '@/types'

const MAX_IMAGE_SIZE = 10 * 1024 * 1024 // 10MB para imagem original
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

interface GameConfig {
  classes: Array<{
    id: string
    name: string
    description?: string
    starting_points?: number
  }>
  attributes: Array<{
    id: string
    name: string
    min: number
    max: number
    default: number
  }>
}

interface EditCharacterDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onEditCharacter: (characterData: Omit<Character, 'id' | 'tableId' | 'userId'>, imageFile?: File, removeImage?: boolean) => void
  character: Character
  tableId: string
  isMaster?: boolean
  /** Mesa mediada: a classe foi sorteada — o aluno não troca (só o mestre). */
  classeLocked?: boolean
}

export function EditCharacterDialog({
  open,
  onOpenChange,
  onEditCharacter,
  character,
  tableId,
  isMaster = false,
  classeLocked = false
}: EditCharacterDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [className, setClassName] = useState('')
  const [isNpc, setIsNpc] = useState(false)
  const [level] = useState(1)
  // (hpTotal removido — a edição preserva character.hpTotal; o HP inicial da
  // mesa vive no game_config e vale só para a criação)
  const [gameConfig, setGameConfig] = useState<GameConfig | null>(null)
  const [dynamicAttributes, setDynamicAttributes] = useState<Record<string, number>>({})
  const [loadingConfig, setLoadingConfig] = useState(false)
  
  // Estados para imagem
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [showCropDialog, setShowCropDialog] = useState(false)
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null)
  const [removeCurrentImage, setRemoveCurrentImage] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Carregar game_config quando o modal abrir
  useEffect(() => {
    if (open && tableId) {
      fetchGameConfig()
    }
  }, [open, tableId])

  // Carregar dados do personagem quando o modal abrir e o gameConfig estiver pronto
  useEffect(() => {
    if (open && character && gameConfig) {
      console.log('🎮 EditCharacterDialog - Carregando dados do personagem:', character.name);
      
      setName(character.name)
      setDescription(character.description || '')
      setClassName(character.className)
      setIsNpc(character.isNpc || false)
      
      // Carregar imagem existente
      setImageFile(null)
      setImagePreview(character.imageUrl || null)
      setRemoveCurrentImage(false)
      
      // Carregar atributos do personagem
      if (character.attributes) {
        console.log('🎮 EditCharacterDialog - Carregando atributos:', character.attributes);
        setDynamicAttributes(character.attributes)
      }
    }
  }, [open, character, gameConfig])

  const fetchGameConfig = async () => {
    setLoadingConfig(true)
    try {
      console.log('🎮 EditCharacterDialog - Fetching game_config for tableId:', tableId)
      
      const config = await apiService.getGameConfig(tableId)
      
      console.log('🎮 Edit Game config loaded:', config)
      console.log('🎮 Edit Classes count:', config?.classes?.length)
      setGameConfig(config)
    } catch (error) {
      console.error('❌ Erro ao carregar game_config:', error)
      toast.error('Não foi possível carregar as configurações da campanha')
    } finally {
      setLoadingConfig(false)
    }
  }

  // Sorteio respeitando o orcamento da classe (starting_points) — espelha o
  // sorteio server-side. Sem classe selecionada o botao fica desabilitado.
  const handleRandomize = () => {
    if (!gameConfig?.attributes || !className) return
    const startingPoints =
      gameConfig.classes?.find(c => c.name === className)?.starting_points ?? 10
    setDynamicAttributes(sortearAtributos(gameConfig.attributes, startingPoints))
  }

  const handleImageClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tipo
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use JPG, PNG, WebP ou GIF.')
      return
    }

    // Validar tamanho
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Arquivo muito grande. Máximo permitido: 10MB')
      return
    }

    // Criar URL para o cropper
    const reader = new FileReader()
    reader.onload = (e) => {
      setCropImageSrc(e.target?.result as string)
      setShowCropDialog(true)
    }
    reader.readAsDataURL(file)
    
    // Limpar input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleCropComplete = (croppedFile: File) => {
    // Criar preview da imagem recortada
    const reader = new FileReader()
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string)
    }
    reader.readAsDataURL(croppedFile)
    
    setImageFile(croppedFile)
    setRemoveCurrentImage(false)
    setCropImageSrc(null)
  }

  const handleRemoveImage = () => {
    setImageFile(null)
    setImagePreview(null)
    setRemoveCurrentImage(true)
  }

  const getInitials = (characterName: string) => {
    return characterName.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2) || '?'
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!name.trim() || !className) {
      toast.error('Preencha nome e classe')
      return
    }

    console.log('🎮 EditCharacterDialog - Enviando atualização:', {
      name: name.trim(),
      className: className,
      description: description.trim(),
      imageFile: imageFile ? 'Arquivo presente' : 'Sem arquivo',
      removeImage: removeCurrentImage,
      attributes: dynamicAttributes
    });

    onEditCharacter({
      name: name.trim(),
      className: className,
      level,
      hpCurrent: character.hpCurrent, // Manter HP atual
      hpTotal: character.hpTotal, // Manter HP total
      points: character.points, // Manter pontos
      description: description.trim(),
      imageUrl: removeCurrentImage ? undefined : character.imageUrl,
      attributes: dynamicAttributes,
      isNpc: isNpc
    }, imageFile || undefined, removeCurrentImage)

    onOpenChange(false)
  }

  const availableClasses = gameConfig?.classes || []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Editar Personagem
          </DialogTitle>
          <DialogDescription>
            Edite as informações do personagem "{character?.name}"
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Foto do Personagem */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Camera weight="fill" size={18} />
              Foto do Personagem
            </Label>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar 
                  className="h-24 w-24 cursor-pointer border-2 border-dashed border-muted-foreground/50 hover:border-primary transition-colors" 
                  onClick={handleImageClick}
                >
                  {imagePreview ? (
                    <AvatarImage src={imagePreview} alt="Preview" />
                  ) : null}
                  <AvatarFallback className="text-3xl bg-muted/50">
                    {name ? getInitials(name) : <Camera size={32} className="text-muted-foreground" />}
                  </AvatarFallback>
                </Avatar>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleImageClick}
                >
                  <Camera size={16} className="mr-2" />
                  {imagePreview ? 'Trocar foto' : 'Selecionar foto'}
                </Button>
                {imagePreview && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveImage}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash size={16} className="mr-2" />
                    Remover
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">
                  JPG, PNG, WebP ou GIF. Máx 10MB.
                </p>
              </div>
            </div>
          </div>
          
          {/* Dialog de Crop */}
          <ImageCropDialog
            open={showCropDialog}
            onOpenChange={setShowCropDialog}
            imageSrc={cropImageSrc || ''}
            onCropComplete={handleCropComplete}
          />

          {/* Nome */}
          <div className="space-y-2">
            <Label htmlFor="name" className="flex items-center gap-2">
              <User weight="fill" size={18} />
              Nome do Personagem
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Digite o nome do personagem"
              required
            />
          </div>

          {/* Tipo de Personagem (apenas para mestre) */}
          {isMaster && (
            <div className="space-y-2">
              <Label htmlFor="characterType" className="flex items-center gap-2">
                <Crown weight="fill" size={18} />
                Tipo de Personagem
              </Label>
              <Select value={isNpc ? 'npc' : 'master'} onValueChange={(value) => setIsNpc(value === 'npc')}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="master">
                    <div className="flex items-center gap-2">
                      <Crown weight="fill" size={16} />
                      <span>Mestre</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="npc">
                    <div className="flex items-center gap-2">
                      <Ghost weight="fill" size={16} />
                      <span>NPC</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Descrição */}
          <div className="space-y-2">
            <Label htmlFor="description">
              Descrição
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o personagem..."
              rows={3}
            />
          </div>

          {/* Classe */}
          <div className="space-y-2">
            <Label htmlFor="className" className="flex items-center gap-2">
              Classe
              {classeLocked && <Lock weight="fill" size={14} className="text-muted-foreground" />}
            </Label>
            {classeLocked && (
              <p className="text-xs text-muted-foreground">
                O papel foi sorteado pelo mestre — não pode ser trocado.
              </p>
            )}
            {loadingConfig ? (
              <div className="text-sm text-muted-foreground">Carregando classes...</div>
            ) : (
              <Select value={className} onValueChange={setClassName} required disabled={classeLocked}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione uma classe" />
                </SelectTrigger>
                <SelectContent>
                  {availableClasses.map((cls) => (
                    <SelectItem key={cls.id} value={cls.name}>
                      {cls.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Atributos Dinâmicos */}
          {gameConfig?.attributes && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  Atributos
                  {!isMaster && <Lock weight="fill" size={14} className="text-muted-foreground" />}
                </Label>
                {isMaster && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRandomize}
                    disabled={!className}
                    title={!className ? 'Escolha uma classe primeiro' : undefined}
                    className="flex items-center gap-2"
                  >
                    <DiceThree size={16} />
                    Aleatorizar
                  </Button>
                )}
              </div>
              {!isMaster && (
                <p className="text-xs text-muted-foreground">
                  Atributos, nível e pontos são definidos pelo mestre. Você pode
                  editar nome, descrição, classe e imagem.
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                {gameConfig.attributes.map((attr) => (
                  <div key={attr.id} className="space-y-2">
                    <Label htmlFor={attr.id} className="text-sm font-medium">
                      {attr.name}
                    </Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        id={attr.id}
                        type="number"
                        min={attr.min}
                        max={attr.max}
                        value={dynamicAttributes[attr.id] || attr.default}
                        disabled={!isMaster}
                        onChange={(e) => setDynamicAttributes(prev => ({
                          ...prev,
                          [attr.id]: parseInt(e.target.value) || attr.default
                        }))}
                        className="w-20"
                      />
                      <span className="text-sm text-muted-foreground">
                        ({attr.min}-{attr.max})
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Botões */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="flex-1"
              disabled={availableClasses.length === 0 || !className}
            >
              Salvar Alterações
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}